import { memo, useCallback } from 'react';

import { useTrackAgentThreadOpened } from '@air/analytics';
import { type AgentSessionResponse } from '@air/server-sdk';

import { WorkspaceAgentPanelMyTasksRowFrame } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowFrame';
import { WorkspaceAgentPanelMyTasksRowIcon } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowIcon';
import { WorkspaceAgentPanelMyTasksRowMeta } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowMeta';
import { WorkspaceAgentPanelMyTasksRowOverflowMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowOverflowMenu';
import { WorkspaceAgentPanelMyTasksRowPreview } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowPreview';
import { WorkspaceAgentPanelMyTasksRowUnarchiveButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowUnarchiveButton';
import { useArchiveWorkspaceAgentTask } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useArchiveWorkspaceAgentTask';
import { usePrivateWorkspaceAgentPanelSessionStatus } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelSessionStatus';
import { useUnarchiveWorkspaceAgentTask } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useUnarchiveWorkspaceAgentTask';
import { useWorkspaceAgentTaskCreatorDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentTaskCreatorDisplay';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';
import { type WorkspaceAgentSessionBucket } from '~/components/v2/components/workspace/workspace-agent-panel/utils/classifyWorkspaceAgentSessionBucket';
import { formatWorkspaceAgentSessionRowTitle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/formatWorkspaceAgentSessionRow';
import { isWorkspaceAgentSessionArchivableFromList } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionArchivableFromList';
import { isWorkspaceAgentSessionNeedsAttention } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionNeedsAttention';
import { getWorkspaceAgentSessionRowPreviewMarkdown } from '~/components/v2/components/workspace/workspace-agent-panel/utils/prepareWorkspaceAgentSessionRowPreviewMarkdown';
import { mapSessionStatusToThreadReopenedStatus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentThreadReopenedStatus';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

const getWorkspaceAgentPanelMyTasksRowTestId = (bucket: WorkspaceAgentSessionBucket) => {
  switch (bucket) {
    case 'needs-response':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_NEEDS_RESPONSE';
    case 'in-progress':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_IN_PROGRESS';
    case 'all-tasks':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_ALL_TASKS';
    case 'archived':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_ARCHIVED';
  }
};

export type PrivateWorkspaceAgentPanelMyTasksRowProps = {
  bucket: WorkspaceAgentSessionBucket;
  session: AgentSessionResponse;
};

type OpenWorkspaceAgentTaskParams = {
  sessionId: string;
  selectSession: (sessionId: string) => void;
  setPanelView: (panelView: WorkspaceAgentPanelView) => void;
};

const openWorkspaceAgentTask = ({ sessionId, selectSession, setPanelView }: OpenWorkspaceAgentTaskParams) => {
  selectSession(sessionId);
  // `showChat` also requests composer focus; opening an existing row should leave focus on the list.
  setPanelView('chat');
};

export const PrivateWorkspaceAgentPanelMyTasksRow = memo(
  ({ bucket, session }: PrivateWorkspaceAgentPanelMyTasksRowProps) => {
    const panelSession = usePrivateWorkspaceAgentPanelSession();
    const { isIdle: isPanelSessionIdle } = usePrivateWorkspaceAgentPanelSessionStatus();
    const { selectSession, sessionId } = panelSession;
    const setPanelView = useWorkspaceAgentPanelStore((store) => store.setPanelView);
    const setOnboardingHandoff = useWorkspaceAgentPanelStore((store) => store.setOnboardingHandoff);
    const clearPendingThreadCreatedLocation = useWorkspaceAgentPanelStore(
      (store) => store.clearPendingThreadCreatedLocation,
    );
    const { archiveTask, isArchivingSession } = useArchiveWorkspaceAgentTask();
    const { isUnarchivingSession, unarchiveTask } = useUnarchiveWorkspaceAgentTask();
    const { trackAgentThreadOpened } = useTrackAgentThreadOpened();
    const { showCreatedBy, getCreatorName } = useWorkspaceAgentTaskCreatorDisplay();
    const isSelected = session.id === sessionId;
    const needsAttention = isWorkspaceAgentSessionNeedsAttention(session);
    const createdByName = showCreatedBy ? getCreatorName(session.createdBy) : undefined;
    const sessionTitle = formatWorkspaceAgentSessionRowTitle(session);
    const sessionPreviewMarkdown = getWorkspaceAgentSessionRowPreviewMarkdown(session);

    const handleOpenTask = useCallback(() => {
      clearPendingThreadCreatedLocation();
      // The scripted example only belongs to the live intro. Opening the thread again shows the real chat.
      setOnboardingHandoff(null);
      openWorkspaceAgentTask({ sessionId: session.id, selectSession, setPanelView });
      trackAgentThreadOpened({
        is_first_run_walkthrough: false,
        session_id: session.id,
        type: 'existing',
        status: mapSessionStatusToThreadReopenedStatus(session.status),
      });
    }, [
      clearPendingThreadCreatedLocation,
      selectSession,
      session.id,
      session.status,
      setOnboardingHandoff,
      setPanelView,
      trackAgentThreadOpened,
    ]);

    const handleArchiveTask = useCallback(() => {
      void archiveTask(session.id);
    }, [archiveTask, session.id]);

    const handleUnarchiveTask = useCallback(() => {
      void unarchiveTask(session.id);
    }, [session.id, unarchiveTask]);

    const isArchivedSession = session.status === 'archived';
    const showArchive = !isArchivedSession;
    const isArchivable = isWorkspaceAgentSessionArchivableFromList(session) && (!isSelected || isPanelSessionIdle);

    return (
      <WorkspaceAgentPanelMyTasksRowFrame
        aria-current={isSelected ? 'true' : undefined}
        data-bucket={bucket}
        data-session-id={session.id}
        data-testid={getWorkspaceAgentPanelMyTasksRowTestId(bucket)}
        icon={<WorkspaceAgentPanelMyTasksRowIcon isSelected={isSelected} session={session} />}
        isSelected={isSelected}
        meta={<WorkspaceAgentPanelMyTasksRowMeta createdByName={createdByName} session={session} />}
        onClick={handleOpenTask}
        overflowMenu={
          isArchivedSession && bucket === 'archived' ? (
            <WorkspaceAgentPanelMyTasksRowUnarchiveButton
              isDisabled={isUnarchivingSession(session.id)}
              onUnarchive={handleUnarchiveTask}
              sessionTitle={sessionTitle}
            />
          ) : (
            <WorkspaceAgentPanelMyTasksRowOverflowMenu
              isArchiveDisabled={!isArchivable || isArchivingSession(session.id)}
              onArchive={handleArchiveTask}
              onOpen={handleOpenTask}
              sessionTitle={sessionTitle}
              showArchive={showArchive}
            />
          )
        }
        preview={<WorkspaceAgentPanelMyTasksRowPreview markdown={sessionPreviewMarkdown} />}
        status={needsAttention ? <span className="sr-only">Needs your attention</span> : undefined}
        title={sessionTitle}
      />
    );
  },
);

PrivateWorkspaceAgentPanelMyTasksRow.displayName = 'PrivateWorkspaceAgentPanelMyTasksRow';
